import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, Phone, ArrowUpRight } from "lucide-react";
import { BOOKING_URL, NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "@/data";
import { EASE } from "@/components/Reveal";
import { MobileMenu } from "@/components/MobileMenu";
import { EmergencyBanner } from "@/components/EmergencyBanner";
import { NavItem } from "@/components/NavItem";
import { track } from "@/hooks/useAnalytics";

const NAV_HEIGHT_OFFSET = -100;
const SCROLLED_THRESHOLD_PX = 40;
const NAV_ENTER = { initial: { y: -80, opacity: 0 }, animate: { y: 0, opacity: 1 }, transition: { duration: 0.9, delay: 0.4, ease: EASE } };

export const scrollToId = (id, duration = 1.4) => {
  const el = document.getElementById(id);
  if (el && window.__lenis) {
    window.__lenis.scrollTo(el, { offset: NAV_HEIGHT_OFFSET, duration });
  } else if (el) {
    el.scrollIntoView({ behavior: "smooth" });
  }
};

export const useSectionNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  return (e, id) => {
    e.preventDefault();
    if (location.pathname === "/") scrollToId(id);
    else navigate("/", { state: { scrollTo: id } });
  };
};


const useScrolled = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLLED_THRESHOLD_PX);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return scrolled;
};

const Nav = () => {
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const goSection = useSectionNav();

  const go = (e, id) => {
    setOpen(false);
    goSection(e, id);
  };

  const goHome = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname !== "/") return navigate("/");
    window.__lenis ? window.__lenis.scrollTo(0, { duration: 1.2 }) : window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <motion.header
      {...NAV_ENTER}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-cream border-b border-stone/80 shadow-[0_8px_24px_-20px_rgba(36,36,33,0.35)]" : "bg-cream border-b border-stone/60"
      }`}
    >
      <EmergencyBanner />
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
        <a href="/" onClick={goHome} data-testid="nav-logo" className="flex items-center">
          <img src="/logo.svg" alt="Glebe Park Dental" className="h-10 w-auto sm:h-11" width="394" height="132" />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavItem
              key={l.label}
              link={l}
              onSection={go}
              testid={`nav-link-${l.id || l.to.slice(1)}`}
              className="font-sans text-base font-medium text-ink transition-colors duration-300 hover:text-forest"
            />
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <a href={PHONE_TEL} data-testid="nav-phone-link" onClick={() => track("call_click", { location: "nav-phone-link" })} className="group flex items-center gap-2 font-sans text-sm text-ink transition-colors duration-300 hover:text-forest">
            <Phone className="h-3.5 w-3.5 text-leaf" />
            {PHONE_DISPLAY}
          </a>
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-book-now-button"
            onClick={() => track("book_click", { location: "nav-book-now-button" })}
            className="group inline-flex items-center gap-2 rounded-md bg-forest px-5 py-2.5 font-sans text-sm font-medium text-cream transition-[background-color,box-shadow] duration-300 hover:bg-pine hover:shadow-[0_8px_24px_-10px_rgba(36,54,47,0.6)]"
          >
            Book Online
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </div>

        <button data-testid="nav-mobile-menu-button" onClick={() => setOpen(!open)} className="text-ink lg:hidden" aria-label="Toggle menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <MobileMenu open={open} onNavigate={go} onClose={() => setOpen(false)} />
    </motion.header>
  );
};

export default Nav;
